import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { DepartmentModel } from '../models/task.models';
import { customValidator } from '../models/custom-validator';
import { ActivatedRoute, Router } from '@angular/router';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { MatIconModule } from '@angular/material/icon';
import { PanelContainerComponent } from 'src/app/shared/content-panels';
import { PanelBodyComponent } from 'src/app/shared/content-panels';

import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { DeleteOperationsDTO, InspectionTaskClassificationAssignmentDTO, TaskConfigurationDTO } from '../models/task.models';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CorpAdminConstants } from '../models/constants/corp-admin.constants';
import { DropDownComponent } from '../shared/drop-down/drop-down.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CorpAdminService } from '../services/corp-admin.service';

@Component({
    selector: 'new-edit-task-management',
    imports: [
    PanelBodyComponent,
    MatIconModule,
    CdkDrag,
    CdkDropList,
    CdkDropListGroup,
    MatTooltipModule,
    DropDownComponent,
    MatSlideToggleModule,
    PanelContainerComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatCheckboxModule
],
    templateUrl: './new-edit-task-management.component.html',
    styleUrl: './new-edit-task-management.component.scss'
})
export class NewEditTaskComponent implements OnDestroy {
  isEditMode: boolean = false;

  //task form group used for task items
  taskFormGroup = this._formBuilder.group({
    taskName: ['', Validators.required],
    required: [false, Validators.required],
    cciToggle: [false],
    cciCode: [''],
    aocToggle: [false],
    aocCode: [''],
    cqiToggle: [false],
    cqiCode: [''],
    qsiToggle: [false],
    qsiCode: [''],
    ebuiltToggle: [false],
    ebuiltCode: [''],
    ebuiltPlusToggle: [false],
    ebuiltPlusCode: [''],
    departments: [<DepartmentModel[]>[], Validators.required],
    primaryDeptId: ['', Validators.required],
    creationDate: [''],
  }, {validators: customValidator});

  //Classification Ids for saving
  CCIid: number;
  AOCid: number;
  CQIid: number;
  QSIid: number;
  ebuiltId: number;
  ebuiltPlusId: number;

  taskId: any = null;

  departments: DepartmentModel[] = [];
  //drag and drop departments
  unassignedList: DepartmentModel[] = [];
  assignedList: DepartmentModel[] = [];

  taskClassifications: any[] = [];
  allTasks: any[] = [];

  cciCodes: string[] = [];
  alreadyUsedCodes: string[] = [];

  selectedPrimaryId: any = '';

  differences: DeleteOperationsDTO[] = [];

  originalData: any = {};
  initialFormValue: string = '';

  get hasFormChanged(): boolean {
    if (!this.isEditMode) return true;
    return JSON.stringify(this.taskFormGroup.getRawValue()) !== this.initialFormValue;
  }
  
  constructor(
    private _formBuilder: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private spinnerService: SpinnerService,
    public corpAdminConstants: CorpAdminConstants,
    private corpAdminService: CorpAdminService
  ) {}

  ngOnInit(): void {
    this.spinnerService.show();
    //set original data
    let allTasks = JSON.parse(localStorage.getItem('configuredTasks') || '[]');
    this.originalData = allTasks.find((task: any) => task.InspectionTask.Id === parseInt(this.activatedRoute.snapshot.paramMap.get('id') || '0')) || {};
    //check if edit mode
    this.activatedRoute.params.subscribe((params) => {
      this.taskId = params['id'];
      this.isEditMode = !!this.taskId;
    });

    //checks for changes on formGroup and if so updates the formGroup value in the sessionStorage
    this.taskFormGroup.valueChanges.subscribe((val: any) => {
      sessionStorage.setItem('taskFormGroup', JSON.stringify(val));
    });

    this.getDepartmentData();
    this.checkUsedCodes();

    const formGroupData = sessionStorage.getItem('taskFormGroup');
    if(formGroupData) {
      this.taskFormGroup.setValue(JSON.parse(formGroupData));

      //populates assigned and unassigned dept lists
      this.assignedList = this.taskFormGroup.value.departments || [];
      let newList = this.departments.filter(dept => !this.assignedList.some((assignedDept: DepartmentModel) => assignedDept.Id === dept.Id));
      this.unassignedList = newList;

      this.spinnerService.hide();
    } else {
      this.spinnerService.hide();
    }

    this.initialFormValue = JSON.stringify(this.taskFormGroup.getRawValue());
  }


  ngOnDestroy(): void {
    //remove the session storage item when user leaves the page
    sessionStorage.removeItem('taskFormGroup');
  }

  //determines which codes are already in use then disables them in the CCI code dropdown
  //TODO -- will need to expand this to all codes once others are added
  checkUsedCodes() {
    this.allTasks = JSON.parse(localStorage.getItem('configuredTasks') || '[]');
    //remove current task - will need to remove the code based on the form group because they can change it while editing
    this.allTasks = this.allTasks.filter((task: any) => task.InspectionTask && task.InspectionTask.Id !== parseInt(this.taskId || '0'));
    this.taskClassifications = JSON.parse(localStorage.getItem('taskClassifications') || '[]');
    let cciCodesArray = this.taskClassifications.filter(classification => classification.SubType === 'CCI');
    this.cciCodes = cciCodesArray.length > 0 ? cciCodesArray[0].Codes.map((code: any) => code.Code) : [];
    this.alreadyUsedCodes = this.allTasks
      .filter((task: any) => 
        task.TaskClassificationAssignments &&
        Array.isArray(task.TaskClassificationAssignments) &&
        task.TaskClassificationAssignments.some((assignment: any) => assignment.ClassificationName === 'CCI')
      )
      .flatMap((task: any) => 
        task.TaskClassificationAssignments
          .filter((assignment: any) => assignment.ClassificationName === 'CCI')
          .flatMap((assignment: any) => assignment.ClassificationCode || [])
      );

    if(this.CCIid === undefined) {
      this.CCIid = this.taskClassifications.find(classification => classification.SubType === 'CCI')?.Id;
      this.AOCid = this.taskClassifications.find(classification => classification.SubType === 'AOC')?.Id;
      this.CQIid = this.taskClassifications.find(classification => classification.SubType === 'CQI')?.Id;
      this.QSIid = this.taskClassifications.find(classification => classification.SubType === 'QSI')?.Id;
      this.ebuiltId = this.taskClassifications.find(classification => classification.SubType === 'eBuilt')?.Id;
      this.ebuiltPlusId = this.taskClassifications.find(classification => classification.SubType === 'eBuilt Plus')?.Id;
    }
    
  }

  getDepartmentData() {
    let data = JSON.parse(localStorage.getItem('departments') || '[]');
    this.departments = data.map((dept: any) => ({ Name: dept.Name, Id: dept.Id }));
    this.unassignedList = [...this.departments];
  }

  //returns true if task name value is the same as the original when user landed on the page
  //returns false if it is different
  originalValueCheck(controlValue: any) {
    return this.originalData && this.originalData.InspectionTask && this.originalData.InspectionTask.Name === controlValue;
  }

  /***
   * Used on editMode only
   * Can reset the task name to original IF user has made changes to task name
   */
  resetTaskName() {
    if(this.originalData && this.originalData.InspectionTask) {
      this.taskFormGroup.get('taskName')?.setValue(this.originalData.InspectionTask.Name);
    }
  }

  //Drop down used for classificiation code
  //Future enhancement will expand to all classifications, currently CCI only
  onCodeChange(selectedCode: string, controlName: string) {
    let currentCode = this.taskFormGroup.get(controlName)?.value;
    if(currentCode) {
      this.alreadyUsedCodes = this.alreadyUsedCodes.filter(code => code !== currentCode);
    }
    this.taskFormGroup.get(controlName)?.setValue(selectedCode);
  }

  //drop action for cdk drag and drop
  drop(event: CdkDragDrop<DepartmentModel[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }

    this.taskFormGroup.get('departments')?.setValue(this.assignedList);
    this.deptReassignCheck(this.assignedList);
  }

  /**
   * confirms that the primary dept is still in the assigned list
   * if not then it is removed from the formGroup so that the save Button will invalidate properly
   */
  deptReassignCheck(deptList: DepartmentModel[]) {
    let primaryDeptId = this.taskFormGroup.get('primaryDeptId')?.value;
    if (primaryDeptId && !deptList.some(dept => dept.Id === Number(primaryDeptId))) {
      this.taskFormGroup.get('primaryDeptId')?.setValue(null);
    }
  }

  //used for both bread crums back buttons and cancel button
  onCancel() {
    //TODO add a save changes popup
    sessionStorage.removeItem('taskFormGroup');
    this.router.navigate(['/corp-admin/task-management']);
  }

  //Saving form group
  onSave(formGroup: any) {
    this.spinnerService.show();
    //format values for the classification assigments
    let classificationAssignments = this.formatClassificationAssigments(formGroup.value);
   
    let saveData: TaskConfigurationDTO = {
      InspectionTask: {
        Id: this.isEditMode && this.taskId ? parseInt(this.taskId) : 0,
        Name: formGroup.value.taskName,
        IsRequired: formGroup.value.required
      },
      TaskClassificationAssignments: classificationAssignments,
      TaskDepartments: formGroup.value.departments.map((dept: DepartmentModel) => ({
        Id: this.originalData?.TaskDepartments?.find((d: any) => d.DepartmentId === dept.Id)?.Id ?? 0,
        DepartmentId: dept.Id,
        IsPrimary: dept.Id === formGroup.value.primaryDeptId
      }))
    };

    if(this.isEditMode && this.taskId) {
      let finalData = this.formatEditSave(saveData);
      saveData.DeleteOperations = finalData[0] || null;
      //Calling the backend service to save the task data
      this.corpAdminService.saveTask(saveData).subscribe({
        next: (response) => {
          console.log('save response: ', response);
          this.spinnerService.hide();
          //TODO add success message
          sessionStorage.removeItem('taskFormGroup');
          this.router.navigate(['/corp-admin/task-management']);
        },
        error: (error) => {
          console.error('save error: ', error);
          this.spinnerService.hide();
          //TODO add error message
        }
      });
    } else {
      this.corpAdminService.saveTask(saveData).subscribe({
        next: (response) => {
          console.log('save response: ', response);
          this.spinnerService.hide();
          //TODO add success message
          sessionStorage.removeItem('taskFormGroup');
          this.router.navigate(['/corp-admin/task-management']);
        },
        error: (error) => {
          console.error('save error: ', error);
          this.spinnerService.hide();
          //TODO add error message
        }
      });
      
    }
  

  }

  //formats the classification assignments to assign toggle/code values
  formatClassificationAssigments(formValue: any) {
    let classificationAssignments: InspectionTaskClassificationAssignmentDTO[] = [];
    if(formValue.cciToggle && formValue.cciToggle === true) {
      classificationAssignments.push({
        Id: this.isEditMode ? this.originalData?.TaskClassificationAssignments?.find((assignment: any) => assignment.ClassificationName === this.corpAdminConstants.CCItoggle)?.Id ?? 0 : 0,
        ClassificationId: Number(this.CCIid),
        ClassificationCode: formValue.cciCode ? [formValue.cciCode] : []
      });
    }
    if(formValue.aocToggle && formValue.aocToggle === true) {
      classificationAssignments.push({
        Id: this.isEditMode ? this.originalData?.TaskClassificationAssignments?.find((assignment: any) => assignment.ClassificationName === this.corpAdminConstants.AOCtoggle)?.Id ?? 0 : 0, 
        ClassificationId: Number(this.AOCid),
        ClassificationCode: formValue.aocCode ? [formValue.aocCode] : []
      });
    }
    if(formValue.cqiToggle && formValue.cqiToggle === true) {
      classificationAssignments.push({
        Id: this.isEditMode ? this.originalData?.TaskClassificationAssignments?.find((assignment: any) => assignment.ClassificationName === this.corpAdminConstants.CQItoggle)?.Id ?? 0 : 0,
        ClassificationId: Number(this.CQIid),
        ClassificationCode: formValue.cqiCode ? [formValue.cqiCode] : []
      });
    }
    if(formValue.qsiToggle && formValue.qsiToggle === true) {
      classificationAssignments.push({
        Id: this.isEditMode ? this.originalData?.TaskClassificationAssignments?.find((assignment: any) => assignment.ClassificationName === this.corpAdminConstants.QSItoggle)?.Id ?? 0 : 0, 
        ClassificationId: Number(this.QSIid),
        ClassificationCode: formValue.qsiCode ? [formValue.qsiCode] : []
      });
    }
    if(formValue.ebuiltToggle && formValue.ebuiltToggle === true) {
      classificationAssignments.push({
        Id: this.isEditMode ? this.originalData?.TaskClassificationAssignments?.find((assignment: any) => assignment.ClassificationName === this.corpAdminConstants.ebuiltToggle)?.Id ?? 0 : 0,
        ClassificationId: Number(this.ebuiltId),
        ClassificationCode: formValue.ebuiltCode ? [formValue.ebuiltCode] : []
      });
    }
    if(formValue.ebuiltPlusToggle && formValue.ebuiltPlusToggle === true) {
      classificationAssignments.push({
        Id: this.isEditMode ? this.originalData?.TaskClassificationAssignments?.find((assignment: any) => assignment.ClassificationName === this.corpAdminConstants.ebuiltPlusToggle)?.Id ?? 0 : 0,
        ClassificationId: Number(this.ebuiltPlusId),
        ClassificationCode: formValue.ebuiltPlusCode ? [formValue.ebuiltPlusCode] : []
      });
    }
    
    return classificationAssignments;

  }

  //Formats the data that will be used for the DeleteOperations array on Edit
  formatEditSave(saveData: TaskConfigurationDTO) {
    if(this.originalData === saveData) {
      return this.originalData;
    } else {
      const taskAssignIds: any[] = [];
      const deptAssignIds: any[] = [];

      if (this.originalData && saveData) {
        // Compare TaskClassificationAssignments
        const origClassAssignments = this.originalData.TaskClassificationAssignments || [];
        const newClassAssignments = saveData.TaskClassificationAssignments || [];

        // Find classification assignments in originalData that are not present in saveData (by ClassificationId)
        const removedClassifications = origClassAssignments
          .filter((orig: any) => !newClassAssignments.some((n: any) => n.Id === orig.Id))
          .map((removed: any) => removed.Id);

        if (removedClassifications.length > 0) {
          taskAssignIds.push(removedClassifications);
        }

        // Compare TaskDepartments
        const origDepartments = this.originalData.TaskDepartments || [];
        const newDepartments = saveData.TaskDepartments || [];

        // Find departments in originalData that are not present in saveData (by DepartmentId)
        const removedDepartments = origDepartments
          .filter((orig: any) => !newDepartments.some((n: any) => n.Id === orig.Id))
          .map((removed: any) => removed.Id);

        if (removedDepartments.length > 0) {
          deptAssignIds.push(removedDepartments);
        }

        this.differences.push({
          InspectionTaskClassificationAssignmentIds: taskAssignIds.length > 0 ? taskAssignIds.flat() : null,
          InspectionTaskDepartmentIds: deptAssignIds.length > 0 ? deptAssignIds.flat() : null
        });

        return this.differences;

      }
    }
  }

}
